/* @flow */

import clsx from 'clsx';
import * as React from 'react';
import { connect } from 'react-redux';

import type { Undefined } from '../../../helpers/jsHelpers/types';
import type { NETGEM_API_V8_METADATA_SCHEDULE } from '../../../libs/netgemLibrary/v8/types/MetadataSchedule';
import type { NETGEM_API_V8_PURCHASE_INFO } from '../../../libs/netgemLibrary/v8/types/PurchaseInfo';
import type { NETGEM_API_V8_REQUEST_RESPONSE } from '../../../libs/netgemLibrary/v8/types/RequestResponse';
import type { CombinedReducers } from '../../../redux/reducers';
import type { Dispatch } from '../../../redux/types/types';
import type { MESSAGE_MODAL_TYPE } from '../../modal/messageModal/MessageModal';

import { getChannelImageId, getChannelName } from '../../../helpers/channel/helper';
import AccurateTimestamp from '../../../helpers/dateTime/AccurateTimestamp';
import { MILLISECONDS_PER_SECOND } from '../../../helpers/dateTime/Format';
import { logError, showDebug } from '../../../helpers/debug/debug';
import { generateDeeplink } from '../../../helpers/debug/deeplink';
import { fireAndForget } from '../../../helpers/jsHelpers/promise';
import Localizer from '../../../helpers/localization';
import { getClosestEvenInteger } from '../../../helpers/maths/maths';
import { Messenger, MessengerEvents } from '../../../helpers/messenger';
import { ParentalControlLockReason } from '../../../helpers/parentalControl/constsAndTypes';
import { type ParentalControlItemState, ParentalControlLockState } from '../../../helpers/parentalControl/constsAndTypes';
import { showUnavailableContentModal } from '../../../helpers/parentalControl/helpers';
import ParentalControlManager from '../../../helpers/parentalControl/parentalControlManager';
import { IMAGE_HEIGHT, IMAGE_WIDTH } from '../../../helpers/ui/constants';
import { getDistributorId } from '../../../helpers/ui/item/distributor';
import { ExtendedItemType, FAKE_EPG_LIVE_PREFIX } from '../../../helpers/ui/item/types';
import { BroadcastStatus, WebAppHelpersLocationStatus, getBroadcastStatus, getLocationStatus } from '../../../helpers/ui/location/Format';
import { filterCredits } from '../../../helpers/ui/metadata/Exclusion';
import { formatSeasonEpisodeNbr, isEpisodeIndexInTitle } from '../../../helpers/ui/metadata/Format';
import { arePurchaseListsDifferent } from '../../../helpers/ui/section/comparisons';
import { getTileCaption, getTileHoverContent } from '../../../helpers/ui/section/tile';
import { Luminosity, Theme, type ThemeType } from '../../../helpers/ui/theme';
import { getVodStatusFromLocations } from '../../../helpers/videofutur/metadata';
import { getVodKind } from '../../../helpers/videofutur/metadata';
import { ignoreIfAborted } from '../../../libs/netgemLibrary/helpers/cancellablePromise/promiseHelper';
import { getItemContentType, getItemType } from '../../../libs/netgemLibrary/v8/helpers/Item';
import getTranslatedText from '../../../libs/netgemLibrary/v8/helpers/Lang';
import { VodKind } from '../../../libs/netgemLibrary/v8/types/FeedItem';
import { ItemContent, ItemLocationKind, ItemType, type NETGEM_API_V8_ITEM_LOCATION } from '../../../libs/netgemLibrary/v8/types/FeedItem';
import { METADATA_KIND_PROGRAM, METADATA_KIND_SERIES, type MetadataKind, type NETGEM_API_V8_METADATA } from '../../../libs/netgemLibrary/v8/types/MetadataProgram';
import { TileConfig, TileContentImageKind, TileOnFocus } from '../../../libs/netgemLibrary/v8/types/WidgetConfig';
import { RegistrationType } from '../../../redux/appRegistration/types/types';
import { hideModal, showMessageModal, showPinModal } from '../../../redux/modal/actions';
import sendV8MetadataRequest from '../../../redux/netgemApi/actions/v8/metadata';
import { type ImageUrlType, getImageUrl } from '../../../redux/netgemApi/actions/v8/metadataImage';
import sendV8MetadataLocationRequest, { getVodLocations } from '../../../redux/netgemApi/actions/v8/metadataLocation';
import { getPurchaseInfoPerAsset } from '../../../redux/netgemApi/actions/v8/purchaseInfo';
import { sendV8RecordingsMetadataRequest } from '../../../redux/netgemApi/actions/v8/recordings';
import { type PIN_DATA_MODAL_TYPE, PinModalKind } from '../../modal/pinModal/PinModalConstsAndTypes';
import { PictoLock } from '../../pictos/Element';
import './Item.css';
import TextScroller from '../../textScroller/TextScroller';
import { getTimeText, getWatchingStatus, renderProgramTitle, renderSeriesTitle } from './helper';
import {
  type CompleteItemPropType,
  type DefaultProps,
  IMAGE_LOAD_TIMEOUT,
  IMAGE_URL_COUNT,
  type ItemPropType,
  type ItemStateType,
  MetadataStatus,
  REFRESH_TIMEOUT,
  type ReduxItemDispatchToPropsType,
  type ReduxItemReducerStateType,
  TRANSPARENT_PIXEL,
} from './ItemConstsAndTypes';
import ItemDecoration from './ItemDecoration';
import ItemOverlay, { ItemOverlayView } from './ItemOverlay';

const InitialState = Object.freeze({
  authority: undefined,
  backgroundImageDisplayIndex: 1,
  backgroundImageLoaded: false,
  broadcastStatus: BroadcastStatus.Unknown,
  caption: new Set<TileConfig>(),
  channelImageUrl: null,
  channelName: null,
  contentType: ItemContent.Unknown,
  displayChannelName: false,
  gameLocationMetadata: null,
  hoverContent: new Set<TileConfig>(),
  imageUrls: [TRANSPARENT_PIXEL, TRANSPARENT_PIXEL],
  isDebugModePlusEnabled: false,
  isFocused: false,
  isImageMissing: false,
  parentalControlState: {
    hasPinCode: false,
    lockReason: undefined,
    lockState: ParentalControlLockState.NotInitialized,
  } as ParentalControlItemState,
  previewCatchupScheduledEventId: null,
  programMetadata: null,
  programMetadataStatus: MetadataStatus.Loading,
  programTitle: '',
  purchaseInfo: null,
  seriesMetadata: null,
  seriesMetadataStatus: MetadataStatus.Loading,
  seriesTitle: '',
  tvLocationMetadata: null,
  vodLocationsMetadata: null,
  vodStatus: null,
});

class Item extends React.PureComponent<CompleteItemPropType, ItemStateType> {
  abortController: AbortController;
  broadcastStatusRefreshTimer: TimeoutID | null;
  imageElement: HTMLElement | null;
  imageHeight: number;
  imageWidth: number;
  imageLoadTimer: TimeoutID | null;
  itemOverlay: ItemOverlayView | null;
  preloadedImage: Image | null;
  timeRefreshTimer: TimeoutID | null;
  // Location Id of purchased location
  viewingHistoryId: ?string;
  // VtiId of purchased location
  vtiId: ?number;

  static defaultProps: DefaultProps = {
    cardData: null,
    filter: undefined,
    isDebugModePlusForced: false,
    isInExploreModal: false,
    isInLiveSection: false,
    isSwiping: false,
    onItemClick: undefined,
    tabIndex: 0,
  };

  constructor(props: CompleteItemPropType) {
    super(props);

    this.abortController = new AbortController();
    this.broadcastStatusRefreshTimer = null;
    this.imageElement = null;
    this.imageHeight = 0;
    this.imageLoadTimer = null;
    this.imageWidth = 0;
    this.itemOverlay = null;
    this.preloadedImage = null;
    this.timeRefreshTimer = null;
    this.viewingHistoryId = null;
    this.vtiId = null;

    this.state = {
      ...InitialState,
      now: AccurateTimestamp.nowInSeconds(),
    };
  }

  componentDidMount() {
    Messenger.on(MessengerEvents.PARENTAL_CONTROL_STATE_UPDATE, this.updateLockState);
    Messenger.on(MessengerEvents.PARENTAL_CONTROL_PIN_UPDATE, this.updateLockState);

    this.update();
  }

  componentDidUpdate(prevProps: CompleteItemPropType, prevState: ItemStateType) {
    const { authenticationToken, isInLiveSection, item, purchaseList } = this.props;
    const { broadcastStatus, contentType } = this.state;
    const { authenticationToken: prevAuthenticationToken, item: prevItem, purchaseList: prevPurchaseList } = prevProps;
    const { broadcastStatus: prevBroadcastStatus, contentType: prevContentType } = prevState;
    const { broadcastStatusRefreshTimer } = this;

    if (authenticationToken !== prevAuthenticationToken) {
      this.handleTokenUpdate(authenticationToken);
    }

    if (item.selectedProgramId !== prevItem.selectedProgramId) {
      this.update();
    }

    if (contentType !== prevContentType) {
      if (!broadcastStatusRefreshTimer) {
        this.updateBroadcastStatus();
      }

      this.loadVodLocations();
    }

    if (broadcastStatus !== prevBroadcastStatus) {
      if (!isInLiveSection) {
        if (broadcastStatus === BroadcastStatus.Live) {
          // Item became live
          this.startTimeRefreshTimer();
        } else {
          // Item is not live anymore
          this.resetTimeRefreshTimer();
        }
      }

      if (prevBroadcastStatus === BroadcastStatus.Unknown) {
        this.loadTVLocationMetadata();
      }
    }

    if (arePurchaseListsDifferent(purchaseList, prevPurchaseList)) {
      this.loadVodLocations();
    }
  }

  componentWillUnmount() {
    const { abortController } = this;

    abortController.abort('Component Item will unmount');

    Messenger.off(MessengerEvents.PARENTAL_CONTROL_STATE_UPDATE, this.updateLockState);
    Messenger.off(MessengerEvents.PARENTAL_CONTROL_PIN_UPDATE, this.updateLockState);

    if (this.preloadedImage) {
      this.preloadedImage.src = '';
      this.preloadedImage.onload = undefined;
      this.preloadedImage = null;
    }

    this.resetTimeRefreshTimer();
    this.resetBroadcastStatusRefreshTimer();
    this.resetImageLoadTimer();
  }

  updateLockState: () => void = () => {
    const { item } = this.props;

    this.setState({ parentalControlState: ParentalControlManager.getItemStateFromItem(item) });
  };

  resetTimeRefreshTimer: () => void = () => {
    if (this.timeRefreshTimer) {
      clearTimeout(this.timeRefreshTimer);
      this.timeRefreshTimer = null;
    }
  };

  resetImageLoadTimer = () => {
    if (this.imageLoadTimer) {
      clearTimeout(this.imageLoadTimer);
      this.imageLoadTimer = null;
    }
  };

  resetBroadcastStatusRefreshTimer = () => {
    if (this.broadcastStatusRefreshTimer) {
      clearTimeout(this.broadcastStatusRefreshTimer);
      this.broadcastStatusRefreshTimer = null;
    }
  };

  startTimeRefreshTimer = () => {
    this.resetTimeRefreshTimer();
    this.timeRefreshTimer = setTimeout(this.updateTime, REFRESH_TIMEOUT);
  };

  startBroadcastStatusRefreshTimer = () => {
    const { broadcastStatus } = this.state;

    this.resetBroadcastStatusRefreshTimer();

    if (broadcastStatus === BroadcastStatus.Past) {
      // A past item will never change, no need to check again
      return;
    }

    this.broadcastStatusRefreshTimer = setTimeout(this.updateBroadcastStatus, REFRESH_TIMEOUT);
  };

  updateTime = () => {
    this.setState({ now: AccurateTimestamp.nowInSeconds() }, this.startTimeRefreshTimer);
  };

  updateBroadcastStatus = () => {
    const {
      item,
      item: { type },
    } = this.props;
    const { contentType } = this.state;

    // Item is an SVOD but is displayed as a catchup (e.g. Okoo)
    this.setState({ broadcastStatus: contentType === ItemContent.NetgemSVOD && type !== ItemType.Series ? BroadcastStatus.Past : getBroadcastStatus(item) }, this.startBroadcastStatusRefreshTimer);
  };

  handleTokenUpdate = (token: string | null) => {
    const { isInLiveSection } = this.props;

    if (token && isInLiveSection) {
      this.updateTime();
      this.updateBroadcastStatus();
    }
  };

  showDebugInfo = () => {
    const {
      itemOverlay,
      props,
      state,
      state: { vodStatus },
    } = this;

    const tvodExpirationTime = vodStatus?.expirationTime ? new Date(vodStatus.expirationTime * MILLISECONDS_PER_SECOND).toLocaleString() : 'n/a';

    showDebug('Section Item', {
      instance: this,
      instanceFields: ['broadcastStatusRefreshTimer', 'imageElement', 'imageHeight', 'imageWidth', 'imageLoadTimer', 'preloadedImage', 'timeRefreshTimer', 'viewingHistoryId', 'vtiId'],
      misc: { tvodExpirationTime, watchingStatus: this.localGetWatchingStatus() },
      props,
      propsFields: ['cardData', 'filter', 'isDebugModePlusForced', 'isInExploreModal', 'isInLiveSection', 'item', 'itemIndex', 'onItemClick', 'tabIndex', 'tileConfig'],
      state,
      stateFields: [
        'authority',
        'backgroundImageDisplayIndex',
        'broadcastStatus',
        'caption',
        'channelImageUrl',
        'channelName',
        'contentType',
        'displayChannelName',
        'gameLocationMetadata',
        'hoverContent',
        'imageUrls',
        'isDebugModePlusEnabled',
        'isFocused',
        'now',
        'parentalControlState',
        'previewCatchupScheduledEventId',
        'programMetadata',
        'programMetadataStatus',
        'programTitle',
        'purchaseInfo',
        'seriesMetadata',
        'seriesMetadataStatus',
        'seriesTitle',
        'tvLocationMetadata',
        'vodLocationsMetadata',
        'vodStatus',
      ],
    });

    itemOverlay?.showDebugInfo();
  };

  localGetWatchingStatus = (): number | null => {
    const { item, viewingHistory, viewingHistoryStatus } = this.props;
    const { contentType, programMetadata, seriesMetadata, vodStatus } = this.state;

    return getWatchingStatus(item, viewingHistory, viewingHistoryStatus, contentType, programMetadata, seriesMetadata, vodStatus);
  };

  getImageWidth = (): number => {
    const { imageElement, imageWidth } = this;

    if (imageWidth === 0 && imageElement) {
      this.imageWidth = getClosestEvenInteger(imageElement.getBoundingClientRect().width);
    }

    return this.imageWidth;
  };

  getImageHeight = (): number => {
    const { imageElement, imageHeight } = this;

    if (imageHeight === 0 && imageElement) {
      this.imageHeight = getClosestEvenInteger(imageElement.getBoundingClientRect().height);
    }

    return this.imageHeight;
  };

  unfocus = () => {
    this.setState({ isFocused: false });
  };

  update = () => {
    const {
      channels,
      isInLiveSection,
      item,
      item: {
        id,
        selectedLocation: { channelId },
        selectedProgramId,
      },
      tileConfig,
      tileConfig: { onFocus },
    } = this.props;
    const { backgroundImageDisplayIndex, imageUrls } = this.state;

    this.resetImageLoadTimer();
    this.resetTimeRefreshTimer();

    this.vtiId = null;

    const itemType = getItemType(id);
    const hoverContent = getTileHoverContent(tileConfig, itemType);
    const caption = getTileCaption(tileConfig, itemType);

    // We copy imageUrls (which is empty at startup) because we transition between previous and new image
    this.setState({
      ...InitialState,
      backgroundImageDisplayIndex,
      caption,
      channelName: getChannelName(channels, channelId),
      hoverContent,
      imageUrls,
      parentalControlState: ParentalControlManager.getItemStateFromItem(item),
    });

    this.loadChannelImage();

    if (selectedProgramId.startsWith(FAKE_EPG_LIVE_PREFIX)) {
      // Live program without info (missing PTF channel)
      return;
    }

    fireAndForget(this.loadImage(), 'Item.loadImage');

    if (onFocus === TileOnFocus.Selection) {
      this.setState({
        programMetadataStatus: MetadataStatus.Loaded,
        seriesMetadataStatus: MetadataStatus.Loaded,
      });
      return;
    }

    this.loadProgramMetadata();
    this.loadSeriesMetadata();

    if (isInLiveSection) {
      // Items in a live section will always be considered 'live', even when starting or finishing
      this.setState({ broadcastStatus: BroadcastStatus.Live });
    }
  };

  updateTitles = () => {
    const { programMetadata, seriesMetadata } = this.state;

    const seriesTitle = getTranslatedText(seriesMetadata?.titles, Localizer.language);
    let title = getTranslatedText(programMetadata?.titles, Localizer.language);
    if (title.toLowerCase() === seriesTitle.toLowerCase()) {
      title = '';
    }

    const index = isEpisodeIndexInTitle(title) ? null : formatSeasonEpisodeNbr(programMetadata);
    const programTitle = `${index ?? ''}${index && title !== '' ? ' : ' : ''}${title}`;

    this.setState({
      programTitle,
      seriesTitle,
    });
  };

  loadChannelImage = () => {
    const {
      channels,
      item: { selectedLocation },
      localGetImageUrl,
      tileConfig: { channelLogo },
    } = this.props;
    const {
      abortController: { signal },
    } = this;

    if (!channelLogo) {
      return;
    }

    const { channelId } = selectedLocation;
    const channelImageId = getChannelImageId(channels, channelId);

    if (!channelImageId) {
      // Universe, channel, channel group, channel without image, etc.
      return;
    }

    localGetImageUrl(
      {
        assetId: channelImageId,
        height: IMAGE_HEIGHT.TileChannelLogo,
        luminosity: Luminosity.Light,
        width: IMAGE_WIDTH.TileChannelLogo,
      },
      signal,
    )
      .then((channelImageUrl: string) => {
        signal.throwIfAborted();

        this.setState({ channelImageUrl });
      })
      .catch((error) => ignoreIfAborted(signal, error));
  };

  loadProgramMetadata = () => {
    const {
      isInLiveSection,
      item,
      item: { selectedProgramId, seriesId },
      localSendV8MetadataRequest,
    } = this.props;
    const {
      abortController: { signal },
    } = this;

    localSendV8MetadataRequest(selectedProgramId, METADATA_KIND_PROGRAM, signal)
      .then((metadata: NETGEM_API_V8_METADATA) => {
        const programMetadata = filterCredits(metadata);

        if (programMetadata.type !== METADATA_KIND_PROGRAM) {
          // Not supposed to happen (only there to make Flow happy)
          return;
        }

        const metadataSeriesId = programMetadata.episodeOf?.id;

        // If the program is part of a series but no series was provided, check in the metadata
        if (!seriesId && metadataSeriesId) {
          this.loadSeriesMetadata(metadataSeriesId);
        }

        const { authority } = programMetadata;

        this.setState(
          {
            authority,
            contentType: getItemContentType(item, authority),
            programMetadata,
            programMetadataStatus: MetadataStatus.Loaded,
          },
          () => {
            this.updateTitles();
            this.loadTVLocationMetadata();
            this.loadGameLocationMetadata();
          },
        );

        // If the program is live, start a timer to update the time display
        if (isInLiveSection) {
          this.startTimeRefreshTimer();
        }
      })
      .catch((error) => ignoreIfAborted(signal, error, () => this.setState({ programMetadataStatus: MetadataStatus.Error })));
  };

  loadSeriesMetadata = (metadataSeriesId?: string) => {
    const {
      item: { seriesId },
      localSendV8MetadataRequest,
    } = this.props;
    const {
      abortController: { signal },
    } = this;

    const id = seriesId ?? metadataSeriesId;

    if (!id) {
      this.setState({ seriesMetadataStatus: MetadataStatus.Loaded });
      return;
    }

    localSendV8MetadataRequest(id, METADATA_KIND_SERIES, signal)
      .then((metadata: NETGEM_API_V8_METADATA) => {
        const seriesMetadata = filterCredits(metadata);

        if (seriesMetadata.type !== METADATA_KIND_SERIES) {
          // Not supposed to happen (only there to make Flow happy)
          return;
        }

        this.setState(
          {
            seriesMetadata,
            seriesMetadataStatus: MetadataStatus.Loaded,
          },
          this.updateTitles,
        );
      })
      .catch((error) => ignoreIfAborted(signal, error, () => this.setState({ seriesMetadataStatus: MetadataStatus.Error })));
  };

  loadImage = async (isNewAttempt?: boolean): Promise<void> => {
    const {
      channels,
      item: {
        id,
        selectedLocation: { channelId },
        selectedProgramId,
      },
      localGetImageUrl,
      tileConfig: { contentImage, contentImageVariant: forcedVariant },
    } = this.props;
    const {
      abortController: { signal },
      imageLoadTimer,
    } = this;

    if (imageLoadTimer) {
      if (isNewAttempt) {
        this.imageLoadTimer = null;
      } else {
        return;
      }
    }

    const width = this.getImageWidth();
    const height = this.getImageHeight();

    if (width === 0 || height === 0) {
      // Image not ready yet
      this.imageLoadTimer = setTimeout(this.loadImage, IMAGE_LOAD_TIMEOUT, true);
      return;
    }

    let assetId: string | null = null;
    let theme: Undefined<ThemeType> = undefined;
    const itemType = getItemType(selectedProgramId);
    if (itemType === ItemType.Channel || contentImage === TileContentImageKind.Channel || selectedProgramId.startsWith(FAKE_EPG_LIVE_PREFIX)) {
      // Channel: Id is the channel Id if content image is 'channel' (e.g. FAST channels) and is item Id if item type is 'channel'
      assetId = getChannelImageId(channels, contentImage === TileContentImageKind.Channel || selectedProgramId.startsWith(FAKE_EPG_LIVE_PREFIX) ? channelId : id);
      theme = Theme.Dark;

      if (assetId === null) {
        // Channel (probably a FAST one) without image: display its name
        this.setState({ channelName: getChannelName(channels, id) || id, displayChannelName: true });
      }
    } else if (itemType === ItemType.Program || itemType === ItemType.Series) {
      // Program or series
      assetId = contentImage === TileContentImageKind.Series ? id : selectedProgramId;
    } else if (itemType === ItemType.ChannelGroup || itemType === ItemType.Universe) {
      // Channel group or universe
      assetId = id;
      theme = Theme.Dark;
    } else {
      // Everything else (i.e. unknown types)
      assetId = id;
    }

    if (assetId === undefined || assetId === null) {
      return;
    }

    try {
      const url: string = await localGetImageUrl(
        {
          assetId,
          forcedVariant,
          height,
          theme,
          width,
        },
        signal,
      );

      signal.throwIfAborted();

      if (url !== '') {
        this.preloadedImage = new Image(width, height);
        this.preloadedImage.onload = () => this.handleImageOnLoad(signal);
        this.preloadedImage.src = url;
      } else {
        // 404 HTTP code is generally not considered as an error for images but in the particular context of a channel image in the zapper, it is
        this.setState({ channelName: getChannelName(channels, id), displayChannelName: true, isImageMissing: true });
      }
    } catch (error) {
      await ignoreIfAborted(signal, error);

      // Channel (probably a FAST one) without image: display its name
      this.setState({ channelName: getChannelName(channels, id), displayChannelName: true, isImageMissing: true });
    }
  };

  handleImageOnLoad = (signal: AbortSignal) => {
    const { backgroundImageDisplayIndex, imageUrls: previousImageUrls } = this.state;

    if (!this.preloadedImage || signal.aborted) {
      // Component unmounted while image was loading
      return;
    }

    this.preloadedImage.onload = undefined;
    const imageUrls = [...previousImageUrls];
    const index = (backgroundImageDisplayIndex + 1) % IMAGE_URL_COUNT;

    if (index === backgroundImageDisplayIndex) {
      // Already been there for this image
      return;
    }

    imageUrls[index] = this.preloadedImage.src;
    this.preloadedImage = null;

    this.setState({
      backgroundImageDisplayIndex: index,
      backgroundImageLoaded: true,
      imageUrls,
    });
  };

  getImageUrl = (): string => {
    const { backgroundImageDisplayIndex, imageUrls } = this.state;

    return imageUrls[backgroundImageDisplayIndex];
  };

  loadPurchaseInfo = () => {
    const {
      item: {
        id,
        purchasable,
        selectedLocation: { channelId, id: selectedLocationId },
      },
      localGetPurchaseInfoPerAsset,
    } = this.props;
    const { contentType } = this.state;
    const {
      abortController: { signal },
    } = this;

    if ((contentType !== ItemContent.VODOrDeeplink && contentType !== ItemContent.NetgemSVOD) || !purchasable || getVodKind(selectedLocationId) === VodKind.SVOD) {
      return;
    }

    if (channelId === undefined) {
      logError(`Channel Id is undefined for item ${id}`);
      return;
    }

    localGetPurchaseInfoPerAsset(id, channelId, signal)
      .then((purchaseInfo: NETGEM_API_V8_PURCHASE_INFO) => {
        signal.throwIfAborted();
        this.setState({ purchaseInfo });
      })
      .catch((error) => ignoreIfAborted(signal, error, () => this.setState({ purchaseInfo: null })));
  };

  loadVodLocations = () => {
    const {
      item: { locations, seriesId },
      localGetVodLocations,
      purchaseList,
    } = this.props;
    const { contentType } = this.state;
    const {
      abortController: { signal },
    } = this;

    this.loadPurchaseInfo();

    if (seriesId) {
      return;
    }

    if ((contentType !== ItemContent.VODOrDeeplink && contentType !== ItemContent.NetgemSVOD) || !locations) {
      return;
    }

    localGetVodLocations(locations, signal)
      .then((vodLocationsMetadata) => {
        signal.throwIfAborted();

        const vodStatus = getVodStatusFromLocations(vodLocationsMetadata, purchaseList);
        const { viewingHistoryId, vtiId } = vodStatus;
        this.viewingHistoryId = viewingHistoryId;
        this.vtiId = vtiId;

        this.setState({
          vodLocationsMetadata,
          vodStatus,
        });
      })
      .catch((error) => ignoreIfAborted(signal, error));
  };

  loadTVLocationMetadata = () => {
    const {
      item,
      item: {
        locType,
        selectedLocation: { id },
      },
      localSendV8MetadataLocationRequest,
      localSendV8RecordingsMetadataRequest,
    } = this.props;
    const { authority, broadcastStatus, contentType } = this.state;
    const {
      abortController: { signal },
    } = this;

    if (!id || contentType === ItemContent.VODOrDeeplink || contentType === ItemContent.NetgemSVOD || broadcastStatus === BroadcastStatus.Unknown) {
      return;
    }

    const locationStatus = getLocationStatus(item, authority);

    const request =
      locationStatus === WebAppHelpersLocationStatus.Recording || (locationStatus === WebAppHelpersLocationStatus.Live && locType === ItemLocationKind.Recording)
        ? localSendV8RecordingsMetadataRequest
        : localSendV8MetadataLocationRequest;

    request(id, signal)
      .then((requestResponse: NETGEM_API_V8_REQUEST_RESPONSE) => {
        signal.throwIfAborted();

        const {
          result,
          result: {
            location: { target },
          },
        } = requestResponse;

        this.setState({
          previewCatchupScheduledEventId: broadcastStatus === BroadcastStatus.Preview ? target : null,
          tvLocationMetadata: result,
        });
      })
      .catch((error) => ignoreIfAborted(signal, error));
  };

  loadGameLocationMetadata = () => {
    const {
      item: {
        selectedLocation: { id },
      },
      localSendV8MetadataLocationRequest,
    } = this.props;
    const { contentType } = this.state;
    const {
      abortController: { signal },
    } = this;

    if (!id || contentType !== ItemContent.Game) {
      return;
    }

    localSendV8MetadataLocationRequest(id, signal)
      .then((requestResponse: NETGEM_API_V8_REQUEST_RESPONSE) => {
        signal.throwIfAborted();

        const { result } = requestResponse;
        this.setState({ gameLocationMetadata: result });
      })
      .catch((error) => ignoreIfAborted(signal, error));
  };

  handleTileMouseOver = () => {
    const {
      isFocused,
      parentalControlState: { lockState },
    } = this.state;

    if (lockState === ParentalControlLockState.NotInitialized || lockState === ParentalControlLockState.Locked) {
      // Content locked by parental control
      return;
    }

    if (!isFocused) {
      this.setState({ isFocused: true });
    }
  };

  handleTileMouseLeave = () => {
    const { isFocused } = this.state;

    if (isFocused) {
      this.unfocus();
    }
  };

  handleTileFocus = (): void => {
    const { itemIndex, onItemFocused } = this.props;

    if (itemIndex !== undefined && onItemFocused !== undefined) {
      onItemFocused(itemIndex);
    }
  };

  handleTileBlur = (event: SyntheticFocusEvent<HTMLElement>): void => {
    const { isFocused } = this.state;
    const { currentTarget, relatedTarget } = event;

    if (relatedTarget instanceof Node && currentTarget instanceof HTMLElement && currentTarget.contains(relatedTarget)) {
      return;
    }

    if (isFocused) {
      this.unfocus();
    }
  };

  executeTileClick = (isUnlockedAdultContent?: boolean) => {
    const {
      cardData,
      isInExploreModal,
      item,
      localHideModal,
      onItemClick,
      tileConfig: { type: tileType },
    } = this.props;
    const { programMetadata, seriesMetadata } = this.state;
    const { vtiId } = this;

    const type = vtiId ? ExtendedItemType.VOD : ExtendedItemType.TV;
    const imageUrl = this.getImageUrl();

    Messenger.emit(MessengerEvents.ITEM_CLICKED, {
      cardData,
      imageUrl,
      isUnlockedAdultContent,
      item,
      onItemClick,
      programMetadata,
      seriesMetadata,
      tileType,
      type,
      vtiId,
    });

    if (isInExploreModal) {
      localHideModal();
    }
  };

  handleTileClick = (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const {
      deviceOS,
      isDebugModeEnabled,
      isSwiping,
      item,
      item: { id: itemId },
    } = this.props;
    const {
      authority,
      contentType,
      isDebugModePlusEnabled: currentIsDebugModePlusEnabled,
      parentalControlState: { lockState },
      programMetadata,
      purchaseInfo,
      seriesMetadata,
      vodLocationsMetadata,
    } = this.state;
    const { altKey, ctrlKey, shiftKey } = event;

    if (isSwiping) {
      return;
    }

    if (isDebugModeEnabled) {
      if (ctrlKey || altKey) {
        if (shiftKey) {
          this.setState({ isDebugModePlusEnabled: !currentIsDebugModePlusEnabled });
        } else {
          this.showDebugInfo();
        }
        return;
      } else if (shiftKey) {
        generateDeeplink({
          authority,
          contentType,
          deviceOS,
          item,
          programMetadata,
          purchaseInfo,
          seriesMetadata,
          vodLocationsMetadata,
        });
        return;
      }
    }

    if ((lockState === ParentalControlLockState.NotInitialized || lockState === ParentalControlLockState.Locked) && getItemType(itemId) !== ItemType.Channel) {
      // Content locked by parental control
      return;
    }

    this.executeTileClick();
  };

  handleTileKeyDown = (event: SyntheticKeyboardEvent<HTMLElement>): void => {
    const {
      tileConfig: { onFocus },
    } = this.props;
    const {
      isFocused,
      parentalControlState: { lockState },
    } = this.state;
    const { key, target } = event;

    if (key === 'Enter' || key === ' ') {
      if (lockState === ParentalControlLockState.NotInitialized || lockState === ParentalControlLockState.Locked) {
        // Content locked by parental control
        event.preventDefault();
        event.stopPropagation();
        this.handleLockOnClick();
        return;
      }

      // If focus is currently inside a button within overlay, let the button handle it
      if (target instanceof HTMLElement && target.closest('button, .buttonFX, [role="button"]:not(.tileContainer, .selection)')) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      if (isFocused || onFocus === TileOnFocus.Selection) {
        // Tile expanded: open card or avenue (depending on edito)
        this.executeTileClick();
      } else {
        // Expand tile
        this.setState({ isFocused: true });
      }
    }
  };

  handleLockOnClick = () => {
    const { item, localShowMessageModal, localShowPinModal } = this.props;
    const {
      parentalControlState: { hasPinCode, lockReason },
    } = this.state;

    if (!hasPinCode && lockReason === ParentalControlLockReason.AdultContent) {
      // Content is locked and cannot be unlocked
      showUnavailableContentModal(localShowMessageModal);
      return;
    }

    if (hasPinCode) {
      // Ask PIN code
      Messenger.once(MessengerEvents.MODAL_CLOSED, this.parentalControlContentUnlockClosedCallback);
      localShowPinModal({ item, kind: lockReason === ParentalControlLockReason.AdultContent ? PinModalKind.UnlockAdultContent : PinModalKind.UnlockContent });
    } else {
      // PIN code creation
      localShowPinModal({ kind: PinModalKind.CreateMaster });
    }
  };

  parentalControlContentUnlockClosedCallback = (isValidPin: boolean): void => {
    if (isValidPin) {
      // Execute regular onClick action
      this.executeTileClick(true);
    }
  };

  renderCaption = (): { caption: React.Node, isFiltered: boolean } => {
    const {
      filter,
      item,
      item: {
        selectedLocation: { channelId },
      },
    } = this.props;
    const { caption, now, programMetadataStatus, programTitle, seriesMetadataStatus, seriesTitle } = this.state;

    const lcFilter = filter?.toLowerCase();
    if (lcFilter !== undefined) {
      const [, channel] = lcFilter.split(':');
      if (channel === undefined) {
        if (programTitle.toLowerCase().indexOf(lcFilter) === -1 && seriesTitle.toLowerCase().indexOf(lcFilter) === -1) {
          // Item removed because program/series title doesn't match filter
          return { caption: null, isFiltered: true };
        }
      } else if (channelId !== undefined && channelId.toLowerCase().indexOf(channel) === -1) {
        // Item removed because channel Id title doesn't match filter
        return { caption: null, isFiltered: true };
      }
    }

    if (caption.size === 0) {
      // No caption at all (e.g. portrait tile)
      return { caption: null, isFiltered: false };
    }

    let titleElement = null;
    let subtitleElement = null;

    if (caption.has(TileConfig.SeriesTitle)) {
      titleElement = renderSeriesTitle(programMetadataStatus, seriesMetadataStatus, seriesTitle);
    }

    const timeText = getTimeText(item, now, caption);

    if (caption.has(TileConfig.ProgramTitle)) {
      const programTitleElement = renderProgramTitle(
        programMetadataStatus,
        seriesMetadataStatus,
        programTitle,
        titleElement ? timeText : null,
        titleElement ? 'secondLineInfo' : '',
        titleElement === null,
      );
      if (titleElement) {
        subtitleElement = programTitleElement;
      } else {
        titleElement = programTitleElement;
      }
    }

    if (!titleElement && !subtitleElement && !timeText) {
      return {
        caption: (
          <div aria-hidden className='textContainer placeholder'>
            <div className='text' />
            <div className='text secondLineInfo' />
          </div>
        ),
        isFiltered: false,
      };
    }

    let secondLineElt = null;
    if (subtitleElement) {
      secondLineElt = <TextScroller style={{ font: '14px var(--regular-font)', height: 'calc(var(--tile-text-height) / 2 * 1px)' }}>{subtitleElement}</TextScroller>;
    } else if (timeText) {
      secondLineElt = <div className='text secondLineInfo'>{timeText}</div>;
    }

    return {
      caption: (
        <div className='textContainer' onClick={this.handleTileClick}>
          <TextScroller aria-hidden style={{ font: '16px var(--semibold-font)', height: 'calc(var(--tile-text-height) / 2 * 1px)' }}>
            {titleElement}
          </TextScroller>
          {secondLineElt}
        </div>
      ),
      isFiltered: false,
    };
  };

  renderSelectionTile = (): React.Node => {
    const {
      isDebugModeEnabled,
      item: {
        id,
        selectedLocation: { channelId },
      },
      tabIndex,
    } = this.props;
    const { channelName, displayChannelName } = this.state;

    const imageUrl = this.getImageUrl();
    const imageStyle = imageUrl ? { backgroundImage: `url(${imageUrl})` } : {};

    const ariaLabel = channelName ?? id.split('/').pop();

    return (
      <div aria-label={ariaLabel} className='sectionItem selection' onBlur={this.handleTileBlur} onFocus={this.handleTileFocus} onKeyDown={this.handleTileKeyDown} role='button' tabIndex={tabIndex}>
        <div className='selectionBorder'>
          <div
            className='tileContainer'
            data-qa-channel-id={channelId ?? id}
            onClick={this.handleTileClick}
            ref={(instance: HTMLElement | null) => {
              this.imageElement = instance;
            }}
            style={imageStyle}
          >
            {isDebugModeEnabled && displayChannelName && channelName !== null ? (
              <TextScroller className='textScroller' scrollOnMount style={{ font: '16px var(--regular-font)', maxHeight: '18px' }}>
                <div>{channelName}</div>
              </TextScroller>
            ) : null}
          </div>
        </div>
      </div>
    );
  };

  renderBackgroundImages = (): React.Node => {
    const { backgroundImageDisplayIndex, imageUrls } = this.state;

    /* eslint-disable react/no-array-index-key */
    return (
      <>
        {imageUrls.map((url, i) => (
          <img alt='' aria-hidden className={clsx('backgroundImage', i === backgroundImageDisplayIndex ? 'visible' : 'hidden')} key={i} src={url} />
        ))}
      </>
    );
    /* eslint-enable react/no-array-index-key */
  };

  renderLock = (): React.Node => {
    const {
      parentalControlState: { lockState },
    } = this.state;

    if (lockState === ParentalControlLockState.NotInitialized || lockState === ParentalControlLockState.Unlocked) {
      return null;
    }

    return (
      <div className='lockWrapper' onClick={this.handleLockOnClick}>
        <PictoLock aria-hidden />
      </div>
    );
  };

  renderMissingImageText = (): React.Node => {
    const { isImageMissing, programTitle, seriesTitle } = this.state;

    if (!isImageMissing) {
      return null;
    }

    return <div className='missingImageText'>{seriesTitle || programTitle}</div>;
  };

  setItemOverlayRef = (instance: ItemOverlayView | null) => {
    this.itemOverlay = instance;
  };

  renderDetailsTile = (): React.Node => {
    const {
      isDebugModeEnabled,
      isDebugModePlusForced = false,
      isInLiveSection,
      item,
      item: {
        selectedLocation: { channelId, id },
      },
      onItemClick,
      tabIndex = 0,
      tileConfig,
      tileConfig: { onFocus },
    } = this.props;
    const {
      authority,
      backgroundImageLoaded,
      broadcastStatus,
      channelImageUrl,
      channelName,
      contentType,
      displayChannelName,
      gameLocationMetadata,
      hoverContent,
      isDebugModePlusEnabled,
      isFocused,
      now,
      parentalControlState: { lockState },
      previewCatchupScheduledEventId,
      programMetadata,
      programMetadataStatus,
      programTitle,
      purchaseInfo,
      seriesMetadata,
      seriesMetadataStatus,
      seriesTitle,
      tvLocationMetadata,
      vodLocationsMetadata,
      vodStatus,
    } = this.state;
    const { viewingHistoryId, vtiId } = this;

    // Information displayed below image, described by caption in tileConfig (series title, program title, date, etc.)
    const { caption, isFiltered } = this.renderCaption();

    if (isFiltered) {
      // User entered a something in the filter box and this item does not match it through its program or series title
      return null;
    }

    const watchingStatus = this.localGetWatchingStatus();

    const distributorId = getDistributorId(vodLocationsMetadata, purchaseInfo, programMetadata);

    // Compute aria-label
    const titleText = seriesTitle || programTitle || '';
    const kind = seriesTitle ? Localizer.localize('accessibility.content.series') : programTitle ? Localizer.localize('accessibility.content.program') : '';
    const labelText = [channelName, kind, titleText].filter(Boolean).join(' ').trim();
    let finalAriaLabel = labelText || channelName || '';

    if (lockState === ParentalControlLockState.Locked) {
      finalAriaLabel = `${finalAriaLabel} ${Localizer.localize('accessibility.navigation.enter_master_pin_to_unlock_content')}`;
    } else if (watchingStatus !== null && watchingStatus > 0) {
      finalAriaLabel = `${finalAriaLabel} ${Localizer.localize('accessibility.content.play_progress', { progress: watchingStatus })}`;
    }

    // 2 possible states for a tile: normal or focused (i.e. hovered)
    return (
      <div className='sectionItem' role='gridcell'>
        <div
          aria-controls={`tile-overlay-${id}`}
          aria-expanded={isFocused}
          aria-label={finalAriaLabel}
          className={clsx('tileContainer', backgroundImageLoaded && 'hideBackgroundColor', isFocused && 'focused', lockState === ParentalControlLockState.Locked && 'locked')}
          data-qa-channel-id={channelId}
          data-qa-location-id={id}
          onBlur={this.handleTileBlur}
          onClick={this.handleTileClick}
          onFocus={this.handleTileFocus}
          onKeyDown={this.handleTileKeyDown}
          onMouseLeave={this.handleTileMouseLeave}
          onMouseOver={this.handleTileMouseOver}
          ref={(instance: HTMLElement | null) => {
            this.imageElement = instance;
          }}
          role='button'
          tabIndex={tabIndex}
        >
          {this.renderMissingImageText()}
          {this.renderBackgroundImages()}
          <ItemDecoration
            broadcastStatus={broadcastStatus}
            channelImageUrl={channelImageUrl}
            contentType={contentType}
            isDebugModePlusEnabled={isDebugModePlusEnabled || isDebugModePlusForced}
            isInLiveSection={isInLiveSection}
            item={item}
            now={now}
            onFocus={onFocus}
            previewCatchupScheduledEventId={previewCatchupScheduledEventId}
            programMetadata={programMetadata}
            tvLocationMetadata={tvLocationMetadata}
            vodLocationsMetadata={vodLocationsMetadata}
            watchingStatus={watchingStatus}
          />
          {isDebugModeEnabled && displayChannelName && channelName !== null ? <div className='channelName'>{channelName}</div> : null}
          <ItemOverlay
            authority={authority}
            broadcastStatus={broadcastStatus}
            contentType={contentType}
            distributorId={distributorId}
            gameLocationMetadata={gameLocationMetadata}
            hoverContent={hoverContent}
            innerRef={this.setItemOverlayRef}
            isInLiveSection={isInLiveSection}
            isVisible={isFocused}
            item={item}
            now={now}
            onClick={this.handleTileClick}
            onItemClick={onItemClick}
            previewCatchupScheduledEventId={previewCatchupScheduledEventId}
            programMetadata={programMetadata}
            programMetadataStatus={programMetadataStatus}
            programTitle={programTitle}
            purchaseInfo={purchaseInfo}
            seriesMetadata={seriesMetadata}
            seriesMetadataStatus={seriesMetadataStatus}
            seriesTitle={seriesTitle}
            tileConfig={tileConfig}
            tvLocationMetadata={tvLocationMetadata}
            viewingHistoryId={viewingHistoryId}
            vodLocationsMetadata={vodLocationsMetadata}
            vodStatus={vodStatus}
            vtiId={vtiId}
          />
          {this.renderLock()}
        </div>
        {caption}
      </div>
    );
  };

  render(): React.Node {
    const {
      item: { selectedProgramId },
      tileConfig: { onFocus },
    } = this.props;

    if (onFocus === TileOnFocus.Details || selectedProgramId.startsWith(FAKE_EPG_LIVE_PREFIX)) {
      // Program, series, etc.: details appear on hover
      return this.renderDetailsTile();
    }

    // Channel, channel group, universe, etc.: tile is selected on hover
    return this.renderSelectionTile();
  }
}

const mapStateToProps = (state: CombinedReducers): ReduxItemReducerStateType => {
  return {
    authenticationToken: state.appRegistration.authenticationToken,
    channels: state.appConfiguration.deviceChannels,
    commercialOffers: state.appConfiguration.rightsCommercialOffers,
    defaultOnItemClick: state.ui.defaultActions ? state.ui.defaultActions.onItemClick : null,
    defaultRights: state.appConfiguration.rightsDefault,
    deviceOS: state.appConfiguration.deviceOS,
    favoriteList: state.ui.favoriteList || [],
    isDebugModeEnabled: state.appConfiguration.isDebugModeEnabled,
    isRegisteredAsGuest: state.appRegistration.registration === RegistrationType.RegisteredAsGuest,
    purchaseList: state.appRegistration.registration === RegistrationType.RegisteredAsGuest ? {} : state.ui.purchaseList,
    userRights: state.appConfiguration.rightsUser,
    viewingHistory: state.ui.viewingHistory,
    viewingHistoryStatus: state.ui.viewingHistoryStatus,
    wishlistStatus: state.ui.wishlistStatus,
  };
};

const mapDispatchToProps = (dispatch: Dispatch): ReduxItemDispatchToPropsType => {
  return {
    localGetImageUrl: (data: ImageUrlType, signal?: AbortSignal): Promise<string> => dispatch(getImageUrl(data, signal)),

    localGetPurchaseInfoPerAsset: (id: string, channelId: string, signal?: AbortSignal): Promise<NETGEM_API_V8_PURCHASE_INFO> => dispatch(getPurchaseInfoPerAsset(id, channelId, signal)),

    localGetVodLocations: (locations: Array<NETGEM_API_V8_ITEM_LOCATION>, signal?: AbortSignal): Promise<Array<NETGEM_API_V8_METADATA_SCHEDULE>> => dispatch(getVodLocations(locations, signal)),

    localHideModal: (): void => dispatch(hideModal()),

    localSendV8MetadataLocationRequest: (assetId: string, signal?: AbortSignal): Promise<NETGEM_API_V8_REQUEST_RESPONSE> => dispatch(sendV8MetadataLocationRequest(assetId, signal)),

    localSendV8MetadataRequest: (assetId: string, type: MetadataKind, signal?: AbortSignal): Promise<NETGEM_API_V8_METADATA> => dispatch(sendV8MetadataRequest(assetId, type, signal)),

    localSendV8RecordingsMetadataRequest: (recordId: string, signal?: AbortSignal): Promise<NETGEM_API_V8_REQUEST_RESPONSE> => dispatch(sendV8RecordingsMetadataRequest(recordId, signal)),

    localShowMessageModal: (messageData: MESSAGE_MODAL_TYPE): void => dispatch(showMessageModal(messageData)),

    localShowPinModal: (data: PIN_DATA_MODAL_TYPE): void => dispatch(showPinModal(data)),
  };
};

const ItemView: React.ComponentType<ItemPropType> = connect(mapStateToProps, mapDispatchToProps)(Item);

export default ItemView;
